import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, ExternalLink, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const Turning65 = () => {
  return (
    <div className="turning-65-page">
      <SEO 
        title="Turning 65 Checklist"
        description="Complete checklist for turning 65 and enrolling in Medicare. Learn when to sign up, avoid late penalties, and make the best Medicare choices. Free consultation available."
        keywords="turning 65, Medicare enrollment, Medicare eligibility, Medicare sign up, Initial Enrollment Period, Medicare penalties, 65th birthday Medicare"
        path="/turning-65"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="/images/turning-65.png" 
                alt="Turning 65" 
                className="rounded-lg shadow-xl w-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">Turning 65 Soon?</h1>
              <p className="text-2xl text-gray-800 mb-8">
                We have developed an easy process to guide you!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Info Bar */}
      <section className="py-4 px-4 bg-yellow-100 border-y-4 border-yellow-400">
        <div className="container mx-auto max-w-6xl">
          <p className="text-xl text-center font-bold text-gray-900">
            Did you know: Delaying Medicare the wrong way, could give you lifelong penalties.<br />
            Let's Talk to make sure this doesn't happen to you.
          </p>
        </div>
      </section>

      {/* When to Sign Up */}
      <section className="py-16 px-4 bg-gray-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">When should you sign up for Medicare?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h4 className="text-3xl font-bold text-teal-600 mb-6 text-center">I will need insurance at 65</h4>
                <div className="space-y-4">
                  <p className="text-lg text-gray-800 font-semibold">
                    You must sign up for Medicare through the Social Security Administration.
                  </p>
                  <p className="text-lg text-gray-800 font-semibold">
                    If you are already receiving Social Security Benefits when you are 65, you will be automatically enrolled in Parts A and B.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:shadow-xl transition-shadow bg-blue-50">
              <CardContent className="p-8">
                <h4 className="text-3xl font-bold text-blue-600 mb-6 text-center">I have employer coverage</h4>
                <div className="space-y-4">
                  <p className="text-lg text-gray-800 font-semibold">
                    IF your employer has over 20 employees then you do not have to enroll in Medicare Parts A or B.
                  </p>
                  <p className="text-lg text-gray-800 font-semibold">
                    However, you keep your employer coverage and add Part A, since you have already paid for this from your FICA taxes.
                  </p>
                  <p className="text-lg text-gray-800 font-semibold">
                    You can DELAY Part B enrollment without penalty if your employer have over 20 employees. You will be required to enroll in Part B when you leave your employer coverage.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Your Medicare Guide - 4 Steps */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Your Medicare Guide</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 */}
            <Card className="border-2 border-teal-300 bg-teal-50">
              <CardContent className="p-6 text-center">
                <div className="text-5xl font-bold text-teal-600 mb-4">1</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Sign up for Medicare Part A & B</h3>
                <p className="text-base text-gray-800 mb-4">Enroll during your Initial Enrollment Period</p>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="https://www.ssa.gov/medicare/sign-up" target="_blank" rel="noopener noreferrer">
                    Sign Up
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="border-2 border-teal-300 bg-teal-50">
              <CardContent className="p-6 text-center">
                <div className="text-5xl font-bold text-teal-600 mb-4">2</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Complete the Confidential Survey</h3>
                <p className="text-base text-gray-800 mb-4">Share your priorities with Paulette</p>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                    Start Here
                  </a>
                </Button>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="border-2 border-teal-300 bg-teal-50">
              <CardContent className="p-6 text-center">
                <div className="text-5xl font-bold text-teal-600 mb-4">3</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Review Your Options</h3>
                <p className="text-base text-gray-800 mb-4">Receive personalized plan comparisons</p>
              </CardContent>
            </Card>

            {/* Step 4 */}
            <Card className="border-2 border-teal-300 bg-teal-50">
              <CardContent className="p-6 text-center">
                <div className="text-5xl font-bold text-teal-600 mb-4">4</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Schedule Your Call</h3>
                <p className="text-base text-gray-800 mb-4">Let's discuss together</p>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                    Book Now
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Medicare Checklist */}
      <section className="py-16 px-4 bg-teal-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Medicare Checklist</h2>
          <Card className="border-2 border-teal-300 bg-white">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <p className="font-bold text-xl text-gray-900">Know Your Enrollment Window</p>
                    <p className="text-lg text-gray-800 mt-1">3 months before, your birth month, 3 months after turning 65</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <p className="font-bold text-xl text-gray-900">Gather Required Documents</p>
                    <p className="text-lg text-gray-800 mt-1">Social Security number, proof of citizenship or legal residency</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <p className="font-bold text-xl text-gray-900">Review Your Medications</p>
                    <p className="text-lg text-gray-800 mt-1">Make a list to ensure they're covered by your chosen plan</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <p className="font-bold text-xl text-gray-900">Check Your Doctor's Network</p>
                    <p className="text-lg text-gray-800 mt-1">Ensure your preferred doctors accept Medicare or your chosen plan</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <p className="font-bold text-xl text-gray-900">Decide on Additional Coverage</p>
                    <p className="text-lg text-gray-800 mt-1">Medicare Advantage, Supplement, or Prescription Drug Plan</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Common Medicare Mistakes */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Avoid These Common Medicare Mistakes</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Mistake 1 */}
            <Card className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl text-red-600 font-bold">✗</span>
                  <h3 className="text-xl font-bold text-gray-900">Missing your enrollment window</h3>
                </div>
                <p className="text-lg text-gray-800">This results in lifetime penalties.</p>
              </CardContent>
            </Card>

            {/* Mistake 2 */}
            <Card className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl text-red-600 font-bold">✗</span>
                  <h3 className="text-xl font-bold text-gray-900">Missing your Guarantee Issue timeframe for enrollment into a Supplement Plan</h3>
                </div>
                <p className="text-lg text-gray-800">Enroll in Medigap during your 6-month Guarantee Issue period to avoid medical underwriting.</p>
              </CardContent>
            </Card>

            {/* Mistake 3 */}
            <Card className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl text-red-600 font-bold">✗</span>
                  <h3 className="text-xl font-bold text-gray-900">Not adding a Part D plan to your Supplement Plan</h3>
                </div>
                <p className="text-lg text-gray-800">Supplement plans don't include prescription coverage - you must add Part D separately.</p>
              </CardContent>
            </Card>

            {/* Mistake 4 */}
            <Card className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl text-red-600 font-bold">✗</span>
                  <h3 className="text-xl font-bold text-gray-900">Not reviewing your plan annually</h3>
                </div>
                <p className="text-lg text-gray-800">Plans change every year - review during Annual Enrollment.</p>
              </CardContent>
            </Card>

            {/* Mistake 5 */}
            <Card className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl text-red-600 font-bold">✗</span>
                  <h3 className="text-xl font-bold text-gray-900">Choosing a plan without comparing options</h3>
                </div>
                <p className="text-lg text-gray-800">Work with an agent to compare all available plans.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">Need Help Navigating Medicare?</h2>
          <p className="text-2xl text-gray-800 mb-8">
            Contact us for personalized guidance through the enrollment process
          </p>
          <div className="space-y-4">
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-2xl px-12 py-8">
              <a href="tel:734.209.6712">
                <Phone className="mr-2" />
                Call: 734.209.6712
              </a>
            </Button>
            <div className="flex items-center justify-center gap-2 text-xl text-gray-800">
              <Mail size={24} />
              <span>Email 24/7: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700 font-semibold">Paulette@llplans.com</a></span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <ContactForm />

      {/* Need Help Section with Email CTA */}
      <section className="py-16 px-4 bg-teal-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Need Help Navigating Medicare?</h2>
          <p className="text-2xl text-gray-700 mb-8">Get Our Medicare Checklist</p>
          <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white text-xl py-8 px-12">
            <a href="mailto:Paulette@llplans.com">
              <Mail className="mr-3" size={24} />
              Email Us for the Checklist
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Turning65;
